import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles } from 'app/theme'
import { useParallax } from 'app/utils/customFunctions'
import useMediaQuery from 'app/utils/useMediaQuery'
import { motion, useScroll } from 'framer-motion'
import React, { memo, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { Overlay } from '../Overlay'

export interface Props {
  claim?: string
  image?: ImageProps
  mobileImage?: ImageProps
  payoff?: string
  variant?: Variant
}

export const Hero = memo(function Hero({
  claim,
  image,
  mobileImage,
  payoff,
  variant = 'default',
}: Props) {
  if (!image) {
    return null
  }

  const [containerRef, isContainerVisible] = useInView({
    triggerOnce: true,
  })

  const { scrollYProgress } = useScroll({
    offset: ['start end', '100vh'],
  })

  const parallax = useParallax(scrollYProgress, [-100, 100])
  const parallaxText = useParallax(scrollYProgress, [100, -100])

  const isMobile = useMediaQuery('(max-width: 1199px)')

  const [heroImage, setHeroImage] = useState<any>(
    isMobile ? mobileImage : image,
  )

  useEffect(() => {
    isMobile ? setHeroImage(mobileImage) : setHeroImage(image)
  }, [heroImage, isMobile])

  return (
    <>
      <Container ref={containerRef} variant={variant}>
        {variant !== 'compact' ? <Overlay /> : null}

        <Background>
          <Parallax style={isContainerVisible ? { y: parallax } : {}}>
            <Image {...heroImage} />
          </Parallax>
        </Background>

        {variant !== 'compact' ? (
          <Wrapper>
            <Text style={{ y: parallaxText }}>
              {payoff ? <Payoff tag="h1" text={payoff} /> : null}

              {claim ? (
                <FadeInUp delay={0.4} y={40}>
                  <Claim>{claim}</Claim>
                </FadeInUp>
              ) : null}
            </Text>
          </Wrapper>
        ) : null}
      </Container>
    </>
  )
})

const Background = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
`

const Parallax = styled(motion.div)`
  height: 100%;
  position: relative;

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    transform: none;
  }
`

const Wrapper = styled.div`
  position: absolute;
  right: 5vw;
  bottom: 5.1875rem;
  left: 5vw;
  z-index: 4;

  @media (max-width: 1199px) {
    right: 2rem;
    bottom: 6.125rem;
    left: 2rem;
  }
`

const Text = styled(motion.div)`
  @media (max-width: 1199px) {
    text-align: center;
    transform: none !important;
  }
`

const Payoff = styled(AnimatedTitle)`
  ${styles.title.large}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
`

const Claim = styled.h2`
  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.75rem;
  font-weight: 300;
  line-height: 2.1875rem;

  @media (max-width: 1199px) {
    font-size: 1.5rem;
    line-height: 2rem;
    margin-top: 0.25rem;
  }
`

const Container = styled.section<ContainerProps>`
  width: 100%;
  height: 100svh;
  max-height: -webkit-fill-available;
  margin: auto;
  overflow: hidden;
  position: relative;

  ${({ variant }) => {
    switch (variant) {
      case 'compact':
        return css`
          width: calc(100% - 3rem);
          height: 50vh;
          margin-top: 4.5rem;

          ${Parallax} {
            transform: none !important;
          }

          @media (max-width: 1199px) {
            aspect-ratio: 1.7;
            width: calc(100% - 2rem);
            height: auto;
            margin-top: 4rem;
          }
        `
      case 'widget':
        return css`
          ${Wrapper} {
            @media (max-width: 1199px) {
              bottom: 9.6875rem;
            }
          }
        `
    }
  }}
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'compact' | 'widget'
